<template>
    <div class="responsibilities-section">
        <Title>Responsibilities</Title>
        <p v-if="!responsibilities?.length" class="no-responsibilities">
            No responsibilities found for this role. Responsibilities are
            automatically derived from tasks belonging to process lanes assigned
            to this role.
        </p>
        <DataTable
            ref="responsibilitiesTable"
            v-else
            :value="responsibilities"
            class="responsibilities-table"
            selectionMode="single"
            @row-click="
                (event: DataTableRowClickEvent) => {
                    const taskLink = `task-link-${event.data.identity}`;
                    responsibilitiesTable.$el
                        .querySelector(`#${taskLink}`)
                        ?.click();
                }
            "
        >
            <Column field="name" header="Task">
                <template #body="{ data }">
                    <RouterLink
                        :id="`task-link-${data.identity}`"
                        class="task-link"
                        :to="{
                            name: 'processElementPage',
                            params: {
                                orgName: route.params.orgName,
                                stekName: route.params.stekName,
                                elementId: data.identity,
                                elementType: 'task',
                            },
                        }"
                    >
                        <div class="task-cell" :data-identity="data.identity">
                            <span>{{ data.name }}</span>
                        </div>
                    </RouterLink>
                </template>
            </Column>
            <Column field="process_name" header="Related Process">
                <template #body="{ data }">
                    <span class="process-name">
                        {{ data.process_name }}
                    </span>
                </template>
            </Column>
        </DataTable>
    </div>
    <template
        v-for="responsibility in responsibilities"
        :key="responsibility.identity"
    >
        <Popup
            v-if="!isEditing && responsibility.identity"
            :anchor="`tr:has(a#task-link-${responsibility.identity})`"
        >
            <GenericCard
                :entityId="responsibility.identity"
                :entityType="EntityType.TASK"
                :showPath="true"
            />
        </Popup>
    </template>
</template>

<script setup lang="ts">
import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useRoleManager } from "@/composables/role-manager";
import { EntityType } from "@/stores/entity-store";
import Column from "primevue/column";
import type { DataTableRowClickEvent } from "primevue/datatable";
import DataTable from "primevue/datatable";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import Title from "../Title.vue";

const route = useRoute();
const responsibilitiesTable = ref();
const { role } = useRoleManager();
const responsibilities = computed(() => role.value?.responsibilities);

const { isEditing } = useEditManager();
</script>

<style scoped lang="scss">
.responsibilities-section {
    margin-top: 1rem;
}

.no-responsibilities {
    color: var(--text-color-secondary);
    font-style: italic;
    margin: 1rem 0;
}

.responsibilities-table {
    margin: 1rem 0;
}

.task-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.process-name {
    color: var(--text-color-secondary);
}

.task-link {
    text-decoration: none;
}
</style>
